import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import type { Shift } from './crew.ts';
import { ApiProblem } from './me.ts';
import type { Offer } from './offers.ts';
import type { Task } from './tasks.ts';
import type { TimeOffEntry } from './time-off.ts';

// CB-REQ OPS-03 at v1.998, CB-CONV §12 crew part 4B: the roster as the one page. One read draws it
// all — the crew by name, the tasks, the shifts and the open offers — and the Select mode's actions
// do several at once, each on its own, so whatever is refused is named and the rest stand.

export type RosterPage = {
  people: components['schemas']['RosterPersonView'][];
  tasks: Task[];
  shifts: Shift[];
  offers: Offer[];
  /** CRW-02 at v1.9106: everyone's unavailability and leave in the window. */
  timeOff: TimeOffEntry[];
};
export type AssignedMany = Omit<components['schemas']['AssignedManyView'], 'assigned'> & {
  assigned: Task[];
};
export type BulkAction = 'publish' | 'confirm' | 'cancel';
export type BulkDone = { done: Shift[]; refused: components['schemas']['BulkRefusedView'][] };

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

/** The window's every part, refreshed each minute and on coming back to the page. */
export const rosterPageQuery = (
  from: string,
  to: string,
  filter: { jobIds: string[]; venueIds: string[] },
) =>
  queryOptions({
    queryKey: ['roster', 'page', from, to, filter],
    queryFn: async (): Promise<RosterPage> => {
      const { data, error } = await api.GET('/v1/roster', {
        params: { query: { from, to, jobId: filter.jobIds, venueId: filter.venueIds } },
      });
      if (error) throw problem(error, 'Could not load the roster.');
      return data as RosterPage;
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

/** Several tasks onto one shift. A mismatch comes back apart, to be sent again confirmed (OPS-10). */
export async function assignTasks(taskIds: string[], shiftId: string, confirmMismatch = false) {
  const { data, error } = await api.POST('/v1/tasks/assign', {
    body: { taskIds, shiftId, confirmMismatch },
  });
  if (error) throw problem(error, 'Could not assign those tasks.');
  return data as AssignedMany;
}

/** Everyone off these tasks; the records stay. */
export async function clearTasks(taskIds: string[]) {
  const { data, error } = await api.POST('/v1/tasks/clear', { body: { taskIds } });
  if (error) throw problem(error, 'Could not take everyone off those tasks.');
  return data as Task[];
}

/** Publish, confirm or cancel several shifts, each as one would be. */
export async function bulkShifts(action: BulkAction, shiftIds: string[]) {
  const { data, error } = await api.POST('/v1/shifts/bulk', { body: { action, shiftIds } });
  if (error) throw problem(error, 'Could not change those shifts.');
  return data as BulkDone;
}
